import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject, viewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, EMPTY, catchError, finalize, switchMap, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Loading } from '@components/ui/loading/loading';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { MsfSelect } from '@components/select/select';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { MakeSubscriptionDialogComponent } from '@app/pages/admin/components/make-subscription-dialog/make-subscription-dialog';
import { defaultSubscriptionNameFromAssignment } from '@app/pages/admin/components/make-subscription-dialog/make-subscription-dialog.util';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { OperationMonitoringService as OperationMonitoringApiService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import { operationDetailsUrl, operationDetailsUrlFromHash } from '@app/pages/admin/operation-details.util';
import { assignmentRowTitle, orchestrationSatelliteProductUrl } from '@app/pages/admin/orchestration-identity.util';
import { assignmentHasSatelliteProducts, assignmentStartApiError, isOrchestrationProbeProcess, processesVisibleOnAssignmentsList } from '@app/pages/admin/assignment-start.util';
import {
  AssignmentResultFilter,
  assignmentResultFilterAfterCreate,
  assignmentResultFilterQuery,
  formatAssignmentResultTooltip,
  readAssignmentResultCount,
} from '@app/pages/admin/assignment-result-count.util';
import { AssignmentResultFilterComponent } from '@app/pages/admin/components/assignment-result-filter/assignment-result-filter';
import { AssignmentResultCellComponent } from '@app/pages/admin/components/assignment-result-cell/assignment-result-cell';
import { AssignmentStartFormComponent } from '@app/pages/admin/orchestration-process-page/components/assignment-start-form/assignment-start-form';
import { MonitoredOperationListItemResponse } from '@6grain/masofa-monolith-api-client/common/model/monitoredOperationListItemResponse';
import { SatelliteAssignmentProductResponse } from '@6grain/masofa-monolith-api-client/common/model/satelliteAssignmentProductResponse';
import { StartAssignmentProcessRequest } from '@6grain/masofa-monolith-api-client/common/model/startAssignmentProcessRequest';
import { CustomPaginatorComponent } from '../../Ui-Kit/custom-paginator/custom-paginator.component';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import {
  AssignmentTableFilters,
  assignmentTableFiltersNeedReload,
  parseAssignmentTableFilterQuery,
  serializeAssignmentTableFilterQuery,
} from '@app/pages/admin/assignment-table-filter-query.util';
import {
  ASSIGNMENT_STATE_FILTERS,
  AssignmentStateFilter,
  assignmentStateSeverity,
  canCancelAssignment,
  canRestartAssignment,
  createdRangeApiBounds,
  TagSeverity,
} from '@app/pages/admin/orchestration-process-page/orchestration-process-page.util';
import { canonicalProcessKey } from '@app/pages/admin/orchestration-process-keys.util';

interface ProcessDefinition {
  key: string;
  displayName?: string;
  description?: string;
  version: number;
}

type AssignmentRow = MonitoredOperationListItemResponse & {
  resultCount: number | null;
  resultTooltip: string | null;
  displayTitle: string;
  stateSeverity: TagSeverity;
  canCancel: boolean;
  canRestart: boolean;
  hasSatelliteProducts: boolean;
};

@Component({
  selector: 'app-assignments-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    TagModule,
    TooltipModule,
    Loading,
    PrimaryButton,
    SecondaryButton,
    MsfSelect,
    MakeSubscriptionDialogComponent,
    CopyableIdentifierComponent,
    DateFormatLangPipe,
    AssignmentStartFormComponent,
    AssignmentResultFilterComponent,
    AssignmentResultCellComponent,
    OrchestrationTableHeadingComponent,
    DatePickerComponent,
    CustomPaginatorComponent,
  ],
  templateUrl: './assignments-page.html',
  styleUrls: [
    '../process-subscriptions-page/process-subscriptions-page.scss',
    './assignments-page.scss',
    '../assignment-result-count.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class AssignmentsPageComponent implements OnInit, OnDestroy {
  private readonly assignments = inject(AssignmentProcessApiService);
  private readonly monitoring = inject(OperationMonitoringApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  readonly translate = inject(TranslateService);
  private readonly startForm = viewChild(AssignmentStartFormComponent);
  private readonly subscriptionDialog = viewChild(MakeSubscriptionDialogComponent);

  processes: ProcessDefinition[] = [];
  processOptions: { id: string; label: string; key: string }[] = [];
  operations: AssignmentRow[] = [];
  searchQuery = '';
  emptyListMessage = 'No assignments found';
  products: Array<SatelliteAssignmentProductResponse & { analysisUrl: string | null }> = [];
  loading = false;
  saving = false;
  dialogVisible = false;
  productsVisible = false;
  cancellationVisible = false;
  cancellationSaving = false;
  cancellationAssignmentId = '';
  restartVisible = false;
  restartSaving = false;
  restartAssignmentId = '';
  copyVisible = false;
  copySaving = false;
  copyAssignmentId = '';
  copyProcessKey = '';
  errorMessage = '';
  private openCreateFromQuery = this.route.snapshot.queryParamMap.get('create') === '1';
  processKey = 'satellite.find-products';
  processFilter: string | null = null;
  resultFilter: AssignmentResultFilter = 'positive';
  stateFilter: AssignmentStateFilter = 'all';
  createdRange: Date[] | null = null;
  stateChips: { id: AssignmentStateFilter; label: string; selected: boolean }[] = [];
  page = 1;
  pageSize = 25;
  totalRecords = 0;
  first = 0;
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly canStartForm = computed(() => this.startForm()?.ready() === true);
  private readonly operationsReload$ = new Subject<void>();
  private lastAppliedFilters: AssignmentTableFilters | null = null;

  ngOnInit(): void {
    this.searchQuery = this.route.snapshot.queryParamMap.get('search') ?? '';
    const detailsUrl = operationDetailsUrlFromHash(this.route.snapshot.fragment);
    if (detailsUrl) {
      void this.router.navigateByUrl(detailsUrl);
      return;
    }
    this.loadProcesses();
    this.rebuildStateChips();
    this.operationsReload$.pipe(
      switchMap(() => {
        this.loading = true;
        this.errorMessage = '';
        this.cdr.markForCheck();
        const search = this.searchQuery.trim() || undefined;
        const state = this.stateFilter === 'all' ? undefined : this.stateFilter;
        const dates = createdRangeApiBounds(this.createdRange);
        return this.monitoring.monitoringOperationsGet(
          'Assignment',
          state,
          undefined,
          undefined,
          search,
          this.processFilter?.trim() || undefined,
          undefined,
          undefined,
          dates.createdFrom,
          dates.createdTo,
          this.page,
          this.pageSize,
          assignmentResultFilterQuery(this.resultFilter),
        ).pipe(
          catchError(error => this.keepListOnGetError(error)),
          finalize(() => {
            this.loading = false;
            this.cdr.markForCheck();
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe(response => {
      this.operations = (response.items ?? []).map(item => this.toAssignmentRow(item));
      this.totalRecords = response.totalCount ?? 0;
      this.page = response.page ?? this.page;
      this.pageSize = response.pageSize ?? this.pageSize;
      this.first = (this.page - 1) * this.pageSize;
      this.emptyListMessage = this.searchQuery.trim() ? 'No catalog matches' : 'No assignments found';
      if (this.openCreateFromQuery) {
        this.dialogVisible = true;
        this.openCreateFromQuery = false;
      }
      this.cdr.markForCheck();
    });
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const parsed = parseAssignmentTableFilterQuery(params);
      const shouldReload = assignmentTableFiltersNeedReload(this.lastAppliedFilters, parsed);
      this.resultFilter = parsed.result;
      this.stateFilter = parsed.state;
      this.createdRange = parsed.createdRange;
      this.processFilter = parsed.process || null;
      this.rebuildStateChips();
      this.lastAppliedFilters = parsed;
      if (params.get('group') != null || params.get('g') != null) {
        this.syncAssignmentFilterQuery();
      }
      if (!shouldReload) {
        this.cdr.markForCheck();
        return;
      }
      this.resetToFirstPage();
      this.reloadOperations();
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.operationsReload$.complete();
  }

  load(): void {
    this.reloadOperations();
  }

  private loadProcesses(): void {
    this.assignments.assignmentsProcessesGet().pipe(takeUntil(this.destroy$)).subscribe({
      next: processes => {
        this.processes = processesVisibleOnAssignmentsList(processes as ProcessDefinition[]);
        this.processOptions = this.processes.map(process => ({
          id: process.key,
          label: process.displayName || process.key,
          key: process.key,
        }));
        const requestedKey = this.route.snapshot.queryParamMap.get('processKey');
        if (requestedKey && this.processes.some(process => process.key === requestedKey)) {
          this.processKey = requestedKey;
        } else if (!this.processes.some(process => process.key === this.processKey)) {
          this.processKey = this.processes[0]?.key ?? '';
        }
        this.operations = this.operations.map(item => this.toAssignmentRow(item));
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Failed to load assignments';
        this.cdr.markForCheck();
      },
    });
  }

  private toAssignmentRow(item: MonitoredOperationListItemResponse): AssignmentRow {
    return {
      ...item,
      resultCount: readAssignmentResultCount(item),
      resultTooltip: formatAssignmentResultTooltip(item),
      displayTitle: assignmentRowTitle(item.name, item.operationType, this.processes),
      stateSeverity: assignmentStateSeverity(item.state),
      canCancel: canCancelAssignment(item.state),
      canRestart: canRestartAssignment(item.state),
      hasSatelliteProducts: assignmentHasSatelliteProducts(item.operationType),
    };
  }

  private keepListOnGetError(error: unknown) {
    if (error instanceof HttpErrorResponse && error.status === 0) {
      return EMPTY;
    }
    this.errorMessage = 'Failed to load assignments';
    return EMPTY;
  }

  private reloadOperations(): void {
    this.operationsReload$.next();
  }

  private resetToFirstPage(): void {
    this.page = 1;
    this.first = 0;
  }

  private tableFilters() {
    return {
      result: this.resultFilter,
      state: this.stateFilter,
      createdRange: this.createdRange,
      process: this.processFilter?.trim() ?? '',
    };
  }

  private rebuildStateChips(): void {
    this.stateChips = ASSIGNMENT_STATE_FILTERS.map(id => ({
      id,
      label: id === 'all' ? 'All' : id,
      selected: this.stateFilter === id,
    }));
  }

  private syncAssignmentFilterQuery(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: serializeAssignmentTableFilterQuery(this.tableFilters()),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private showNewAssignment(processKey: string): void {
    this.resultFilter = assignmentResultFilterAfterCreate();
    const key = canonicalProcessKey(processKey);
    this.processFilter = isOrchestrationProbeProcess(key) ? null : (key || null);
    this.rebuildStateChips();
    this.resetToFirstPage();
    this.syncAssignmentFilterQuery();
    this.reloadOperations();
  }

  create(): void {
    this.startForm()?.reset();
    this.dialogVisible = true;
  }

  onCatalogSearch(query: string): void {
    const next = query ?? '';
    if (next === this.searchQuery) {
      return;
    }
    this.searchQuery = next;
    this.resetToFirstPage();
    this.reloadOperations();
  }

  onResultFilterChange(filter: AssignmentResultFilter): void {
    if (this.resultFilter === filter) {
      return;
    }
    this.resultFilter = filter;
    this.syncAssignmentFilterQuery();
  }

  onProcessFilterChange(value: string | null): void {
    const next = value?.trim() || null;
    if ((this.processFilter ?? null) === next) {
      return;
    }
    this.processFilter = next;
    this.syncAssignmentFilterQuery();
  }

  selectStateFilter(filter: AssignmentStateFilter): void {
    if (this.stateFilter === filter) {
      return;
    }
    this.stateFilter = filter;
    this.rebuildStateChips();
    this.syncAssignmentFilterQuery();
  }

  onCreatedRangeChange(value: Date[] | Date | null): void {
    if (Array.isArray(value)) {
      this.createdRange = value.filter((entry): entry is Date => entry instanceof Date);
    } else {
      this.createdRange = null;
    }
    this.syncAssignmentFilterQuery();
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    this.first = first;
    this.pageSize = rows;
    this.page = Math.floor(first / rows) + 1;
    this.reloadOperations();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.resetToFirstPage();
    this.reloadOperations();
  }

  start(): void {
    this.errorMessage = '';
    const built = this.startForm()?.build();
    if (!built) {
      return;
    }
    if (!built.ok) {
      this.errorMessage = built.error;
      return;
    }
    this.saving = true;
    const request: StartAssignmentProcessRequest = { parameters: built.parameters };
    this.assignments.assignmentsProcessesProcessKeyPost(this.processKey, request)
      .pipe(takeUntil(this.destroy$), finalize(() => { this.saving = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: () => { this.dialogVisible = false; this.showNewAssignment(this.processKey); },
        error: error => this.errorMessage = assignmentStartApiError(error, 'Failed to create assignment'),
      });
  }

  showProducts(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id) return;
    this.assignments.assignmentsAssignmentIdSatelliteProductsGet(operation.id, 0, 100).pipe(takeUntil(this.destroy$)).subscribe({
      next: products => {
        this.products = products.map(product => ({
          ...product,
          analysisUrl: orchestrationSatelliteProductUrl(product.id),
        }));
        this.setProductsVisible(true);
      },
      error: () => { this.errorMessage = 'Failed to load assignment products'; this.cdr.markForCheck(); },
    });
  }

  setProductsVisible(visible: boolean): void {
    this.productsVisible = visible;
    if (!visible) {
      this.products = [];
    }
    this.cdr.markForCheck();
  }

  makeSubscription(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id) return;
    this.subscriptionDialog()?.open(
      operation.id,
      defaultSubscriptionNameFromAssignment({
        name: operation.name,
        operationType: operation.operationType,
      }),
    );
  }

  requestCancellation(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id || !this.canCancel(operation)) return;
    this.cancellationAssignmentId = operation.id;
    this.cancellationVisible = true;
  }

  confirmCancellation(): void {
    if (!this.cancellationAssignmentId) return;
    this.cancellationSaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdCancelPost(this.cancellationAssignmentId)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.cancellationSaving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.cancellationVisible = false;
          this.cancellationAssignmentId = '';
          this.load();
        },
        error: error => this.errorMessage = assignmentStartApiError(error, 'Failed to cancel assignment'),
      });
  }

  requestRestart(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id || !this.canRestart(operation)) return;
    this.restartAssignmentId = operation.id;
    this.restartVisible = true;
  }

  confirmRestart(): void {
    if (!this.restartAssignmentId) return;
    this.restartSaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdRestartPost(this.restartAssignmentId)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.restartSaving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.restartVisible = false;
          this.restartAssignmentId = '';
          this.load();
        },
        error: error => this.errorMessage = assignmentStartApiError(error, 'Failed to restart assignment'),
      });
  }

  requestCopy(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id) return;
    this.copyAssignmentId = operation.id;
    this.copyProcessKey = operation.operationType ?? '';
    this.copyVisible = true;
  }

  confirmCopy(): void {
    if (!this.copyAssignmentId) return;
    this.copySaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdCopyPost(this.copyAssignmentId)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.copySaving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.copyVisible = false;
          this.copyAssignmentId = '';
          this.showNewAssignment(this.copyProcessKey);
        },
        error: error => this.errorMessage = assignmentStartApiError(error, 'Failed to copy assignment'),
      });
  }

  openDetails(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id) return;
    void this.router.navigateByUrl(operationDetailsUrl(operation.kind || 'Assignment', operation.id));
  }

  canCancel(operation: MonitoredOperationListItemResponse): boolean {
    return canCancelAssignment(operation.state);
  }

  canRestart(operation: MonitoredOperationListItemResponse): boolean {
    return canRestartAssignment(operation.state);
  }
}
